import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { DocumentJobStatus, JobStatusCountResponse } from '../api';
import { AdminKpiCard } from '../components/AdminKpiCard';
import { AiUsageSection } from '../components/AiUsageSection';
import { PlanBadge } from '../components/PlanBadge';
import { useBillingMetrics } from '../hooks/useBillingMetrics';
import { useOperationalMetrics } from '../hooks/useOperationalMetrics';
import { usePlatformMetrics } from '../hooks/usePlatformMetrics';
import { useTechHealthMetrics } from '../hooks/useTechHealthMetrics';

const STATUS_COLORS: Record<DocumentJobStatus, string> = {
  Queued: '#94a3b8',
  Processing: '#0ea5e9',
  Approved: '#22c55e',
  ReviewRequired: '#f59e0b',
  Rejected: '#f97316',
  Failed: '#ef4444',
};

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return amount.toLocaleString('es-ES', { style: 'currency', currency });
  } catch {
    return `${amount.toLocaleString('es-ES')} ${currency}`;
  }
}

function formatMs(ms: number | null): string {
  if (ms === null) return 'sin datos';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function shortDate(iso: string): string {
  // yyyy-MM-dd -> dd/MM (UI is es-ES; the ISO day field comes from Cosmos SUBSTRING).
  if (iso.length < 10) return iso;
  return `${iso.substring(8, 10)}/${iso.substring(5, 7)}`;
}

export function PlatformDashboardPage(): JSX.Element {
  const summary = usePlatformMetrics();
  const operational = useOperationalMetrics();
  const billing = useBillingMetrics();
  const tech = useTechHealthMetrics();

  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard de plataforma</h1>
        <p className="mt-1 text-sm text-slate-500">Estado operacional, negocio y salud técnica de todos los tenants.</p>
      </header>

      {/* 1. Resumen general */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Resumen general</h2>

        {summary.isError ? (
          <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            No se pudieron cargar los KPIs de tenants.
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <AdminKpiCard label="Tenants totales" value={summary.isLoading ? '…' : summary.data?.totalTenants ?? 0} />
          <AdminKpiCard label="Tenants activos" accent="green" value={summary.isLoading ? '…' : summary.data?.activeTenants ?? 0} />
          <AdminKpiCard
            label="Suspendidos"
            accent={(summary.data?.suspendedTenants ?? 0) > 0 ? 'amber' : 'default'}
            value={summary.isLoading ? '…' : summary.data?.suspendedTenants ?? 0}
          />
          <AdminKpiCard
            label="Bloqueados"
            accent={(summary.data?.blockedTenants ?? 0) > 0 ? 'red' : 'default'}
            value={summary.isLoading ? '…' : summary.data?.blockedTenants ?? 0}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="card p-5">
            <h3 className="text-base font-semibold text-slate-900">Top 5 tenants por volumen mensual</h3>
            {summary.isLoading ? (
              <SkeletonRows />
            ) : (
              <ol className="mt-3 space-y-2">
                {(summary.data?.topTenantsByUsage ?? []).map((t, i) => (
                  <li key={t.tenantId} className="flex items-center gap-3 text-sm">
                    <span className="w-4 text-right text-xs text-slate-400">{i + 1}</span>
                    <span className="flex-1 truncate text-slate-700">{t.name}</span>
                    <span className="tabular-nums text-slate-500">{t.monthlyCount.toLocaleString('es-ES')}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="card p-5">
            <h3 className="text-base font-semibold text-slate-900">Distribución por plan</h3>
            {summary.isLoading ? (
              <SkeletonRows count={3} />
            ) : (
              <ul className="mt-3 space-y-2">
                {(summary.data?.tenantsByPlan ?? []).map((p) => (
                  <li key={p.plan} className="flex items-center gap-3">
                    <PlanBadge plan={p.plan} />
                    <span className="tabular-nums text-sm text-slate-700">{p.count} tenants</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* 2. Operacional */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Operacional</h2>

        {operational.isError ? (
          <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            No se pudieron cargar las métricas operacionales.
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <AdminKpiCard label="Docs últimas 24 h" value={operational.isLoading ? '…' : (operational.data?.docsLast24h ?? 0).toLocaleString('es-ES')} />
          <AdminKpiCard label="Docs últimos 7 d" value={operational.isLoading ? '…' : (operational.data?.docsLast7d ?? 0).toLocaleString('es-ES')} />
          <AdminKpiCard label="Docs últimos 30 d" value={operational.isLoading ? '…' : (operational.data?.docsLast30d ?? 0).toLocaleString('es-ES')} />
          <AdminKpiCard
            label="Tasa éxito 7 d"
            accent={(operational.data?.successRate7d ?? 1) >= 0.95 ? 'green' : 'amber'}
            value={operational.isLoading ? '…' : formatPercent(operational.data?.successRate7d ?? 0)}
          />
        </div>

        <div className="card p-5">
          <h3 className="text-base font-semibold text-slate-900">Volumen diario (últimos 7 días)</h3>
          {operational.isLoading || !operational.data ? (
            <div className="mt-4 h-56 animate-pulse rounded bg-slate-100" />
          ) : operational.data.dailyLast7d.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">Sin datos en este rango.</p>
          ) : (
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={operational.data.dailyLast7d.map((d) => ({ ...d, day: shortDate(d.day) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#0ea5e9" name="Total" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="succeeded" stroke="#22c55e" name="OK" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="failed" stroke="#ef4444" name="Fallos" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="card p-5">
            <h3 className="text-base font-semibold text-slate-900">Errores recientes</h3>
            {operational.isLoading ? (
              <SkeletonRows />
            ) : (operational.data?.recentFailures ?? []).length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">Sin fallos recientes.</p>
            ) : (
              <ul className="mt-3 space-y-2 text-sm">
                {(operational.data?.recentFailures ?? []).map((f) => (
                  <li key={f.jobId} className="border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                    <div className="flex justify-between gap-3">
                      <span className="truncate font-mono text-xs text-slate-500">{f.jobId}</span>
                      <span className="text-xs text-slate-400">{new Date(f.submittedAt).toLocaleString('es-ES')}</span>
                    </div>
                    <p className="mt-0.5 text-slate-700">{f.failureReason ?? 'Sin razón registrada.'}</p>
                    <p className="text-xs text-slate-400">tenant: {f.tenantId}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card p-5">
            <h3 className="text-base font-semibold text-slate-900">Tenants cerca de su cuota (≥ 80%)</h3>
            {operational.isLoading ? (
              <SkeletonRows />
            ) : (operational.data?.tenantsNearQuota ?? []).length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">Ningún tenant en aviso.</p>
            ) : (
              <ul className="mt-3 space-y-2 text-sm">
                {(operational.data?.tenantsNearQuota ?? []).map((t) => (
                  <li key={t.tenantId} className="flex items-center gap-3">
                    <span className="flex-1 truncate text-slate-700">{t.name}</span>
                    <PlanBadge plan={t.licenseType} />
                    <span className={`tabular-nums text-xs ${t.usagePercent >= 100 ? 'text-rose-600 font-semibold' : 'text-amber-600'}`}>
                      {t.usagePercent}% ({t.monthlyDocumentCount.toLocaleString('es-ES')}/{t.monthlyDocumentLimit.toLocaleString('es-ES')})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* 3. Negocio */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Negocio</h2>

        {billing.isError ? (
          <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            No se pudieron cargar las métricas de negocio.
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-3">
          <AdminKpiCard
            label="MRR estimado"
            accent="green"
            value={billing.isLoading || !billing.data ? '…' : formatCurrency(billing.data.estimatedMrr, billing.data.currency)}
            sub="Tenants activos × coste del plan"
          />
          <AdminKpiCard label="Tenants de pago" value={billing.isLoading ? '…' : billing.data?.payingTenants ?? 0} />
          <AdminKpiCard label="Tenants gratuitos" value={billing.isLoading ? '…' : billing.data?.freeTenants ?? 0} />
        </div>

        <div className="card p-5">
          <h3 className="text-base font-semibold text-slate-900">Revenue proyectado por plan</h3>
          {billing.isLoading || !billing.data ? (
            <div className="mt-4 h-56 animate-pulse rounded bg-slate-100" />
          ) : billing.data.byPlan.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Sin tenants activos.</p>
          ) : (
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={billing.data.byPlan.map((p) => ({
                    plan: p.licenseType,
                    tenants: p.tenantCount,
                    revenue: p.projectedMonthlyRevenue,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="plan" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="tenants" fill="#94a3b8" name="Tenants" />
                  <Bar yAxisId="right" dataKey="revenue" fill="#22c55e" name={`Revenue (${billing.data.currency})`} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      {/* 4. Salud técnica */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Salud técnica</h2>

        {tech.isError ? (
          <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            No se pudieron cargar las métricas técnicas.
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <AdminKpiCard
            label="Cola actual"
            accent={(tech.data?.queueDepth ?? 0) > 50 ? 'red' : (tech.data?.queueDepth ?? 0) > 10 ? 'amber' : 'green'}
            value={tech.isLoading ? '…' : tech.data?.queueDepth ?? 0}
            sub="Jobs en Queued + Processing"
          />
          <AdminKpiCard
            label="Latencia media (7 d)"
            value={tech.isLoading ? '…' : formatMs(tech.data?.averageProcessingMs ?? null)}
            sub="CompletedAt − SubmittedAt"
          />
          <AdminKpiCard
            label="Jobs procesados (7 d)"
            value={tech.isLoading ? '…' : (tech.data?.statusBreakdown7d.reduce((s, c) => s + c.count, 0) ?? 0).toLocaleString('es-ES')}
          />
        </div>

        <div className="card p-5">
          <h3 className="text-base font-semibold text-slate-900">Mix de estados (últimos 7 días)</h3>
          {tech.isLoading || !tech.data ? (
            <div className="mt-4 h-56 animate-pulse rounded bg-slate-100" />
          ) : tech.data.statusBreakdown7d.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">Sin actividad reciente.</p>
          ) : (
            <div className="mt-4 grid gap-4 lg:grid-cols-2 lg:items-center">
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={tech.data.statusBreakdown7d}
                      dataKey="count"
                      nameKey="status"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {tech.data.statusBreakdown7d.map((c: JobStatusCountResponse) => (
                        <Cell key={c.status} fill={STATUS_COLORS[c.status]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1 text-sm">
                {tech.data.statusBreakdown7d.map((c) => (
                  <li key={c.status} className="flex items-center gap-3">
                    <span
                      className="inline-block h-3 w-3 rounded"
                      style={{ backgroundColor: STATUS_COLORS[c.status] }}
                    />
                    <span className="flex-1 text-slate-700">{c.status}</span>
                    <span className="tabular-nums text-slate-500">{c.count.toLocaleString('es-ES')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400">
          Latencias y uso por provider están pendientes de instrumentar — la entidad <code>DocumentJob</code> aún no
          registra el provider concreto que atendió cada extracción.
        </p>
      </section>

      <AiUsageSection />
    </div>
  );
}

function SkeletonRows({ count = 5 }: { count?: number }): JSX.Element {
  return (
    <div className="mt-3 space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-6 animate-pulse rounded bg-slate-100" />
      ))}
    </div>
  );
}
